import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  emailChangeConfirmationSchema,
  emailChangeRequestSchema,
  type EmailChangeConfirmationFormValues,
  type EmailChangeRequestFormValues,
} from '@emazy/shared-validation';

import { CustomerProfileApiError } from '../api';
import { useConfirmEmailChange, useStartEmailChange } from '../hooks';

export function EmailChangeDialog({ open, onClose }: { open: boolean; onClose(): void }) {
  const [target, setTarget] = useState('');
  const start = useStartEmailChange();
  const confirm = useConfirmEmailChange();
  const requestForm = useForm<EmailChangeRequestFormValues>({
    resolver: zodResolver(emailChangeRequestSchema),
    defaultValues: { newEmail: '' },
  });
  const confirmationForm = useForm<EmailChangeConfirmationFormValues>({
    resolver: zodResolver(emailChangeConfirmationSchema),
    defaultValues: { newEmail: '', code: '' },
  });
  if (!open) return null;
  const close = () => {
    start.reset();
    confirm.reset();
    setTarget('');
    onClose();
  };
  const error = start.error ?? confirm.error;
  return (
    <div className="dialog-backdrop" role="presentation">
      <section
        className="account-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-dialog-title"
      >
        <button className="account-dialog__close" type="button" aria-label="Close" onClick={close}>
          <i className="pi pi-times" aria-hidden="true" />
        </button>
        <h2 id="email-dialog-title">Change sign-in email</h2>
        {error && (
          <p className="form-alert" role="alert">
            {error instanceof CustomerProfileApiError
              ? error.message
              : 'We could not complete this change. Please try again.'}
          </p>
        )}
        {!target ? (
          <form
            onSubmit={requestForm.handleSubmit(async (values) => {
              await start.mutateAsync(values);
              setTarget(values.newEmail);
              confirmationForm.setValue('newEmail', values.newEmail);
            })}
          >
            <p>We’ll send a verification code. Once confirmed, this becomes your sign-in email.</p>
            <label htmlFor="new-email">New email</label>
            <input
              id="new-email"
              type="email"
              autoComplete="email"
              {...requestForm.register('newEmail')}
            />
            {requestForm.formState.errors.newEmail && (
              <small role="alert">{requestForm.formState.errors.newEmail.message}</small>
            )}
            <button className="button-link button-link--primary" disabled={start.isPending}>
              {start.isPending ? 'Sending…' : 'Send verification code'}
            </button>
          </form>
        ) : (
          <form
            onSubmit={confirmationForm.handleSubmit(async (values) => {
              await confirm.mutateAsync(values);
              close();
            })}
          >
            <p>Enter the six-digit code sent to {target}.</p>
            <input type="hidden" {...confirmationForm.register('newEmail')} />
            <label htmlFor="email-code">Verification code</label>
            <input
              id="email-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              {...confirmationForm.register('code')}
            />
            {confirmationForm.formState.errors.code && (
              <small role="alert">{confirmationForm.formState.errors.code.message}</small>
            )}
            <button className="button-link button-link--primary" disabled={confirm.isPending}>
              {confirm.isPending ? 'Confirming…' : 'Confirm email change'}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
